// ComposerVideoChip.tsx — video phase 2: the attached clip, with where its work has got to.
// A muted, frame-only preview of the picked file (it never plays here), a status line that
// is announced politely, and a real progressbar while compressing/uploading.

import type { CSSProperties } from 'react';
import styled from 'styled-components';
import { TooltipButton } from '@/ui/TooltipButton';
import { Icon } from '@/icons';
import type { ComposerVideo } from './useComposerVideo';

const Chip = styled.div`
  position: relative;
  margin-top: 4px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
  background: ${({ theme }) => theme.color.surfaceRaised};
`;

const Preview = styled.video`
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  background: #000;
`;

const Status = styled.div`
  display: grid;
  gap: 6px;
  padding: 10px 12px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const StatusText = styled.p<{ $error: boolean }>`
  margin: 0;
  color: ${({ theme, $error }) => ($error ? theme.color.danger : theme.color.textMuted)};
`;

const Track = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: calc(var(--p) * 100%);
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.brandFrom},
    ${({ theme }) => theme.color.brandTo}
  );
  transition: width 200ms linear;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Remove = styled(TooltipButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textMuted};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

function statusLine(v: ComposerVideo): string {
  const pct = `${Math.round(v.progress * 100)}%`;
  switch (v.stage) {
    case 'checking':
      return 'Checking your video…';
    case 'preparing':
      return `Compressing on your device… ${pct}`;
    case 'uploading':
      return `Uploading… ${pct}`;
    case 'ready':
      return 'Video ready to post.';
    case 'error':
      return v.error ?? "Couldn't add that video.";
  }
}

export interface ComposerVideoChipProps {
  video: ComposerVideo;
  onRemove: () => void;
  disabled?: boolean;
}

export function ComposerVideoChip({ video, onRemove, disabled }: ComposerVideoChipProps) {
  const working = video.stage === 'preparing' || video.stage === 'uploading';
  const label = statusLine(video);
  return (
    <Chip data-testid="composer-video-chip" data-stage={video.stage}>
      <Preview
        src={video.previewUrl}
        muted
        playsInline
        preload="metadata"
        aria-label={`Selected video: ${video.file.name}`}
      />
      <Status>
        <StatusText
          role={video.stage === 'error' ? 'alert' : 'status'}
          $error={video.stage === 'error'}
        >
          {label}
        </StatusText>
        {working && (
          <Track
            role="progressbar"
            aria-label={video.stage === 'preparing' ? 'Compressing video' : 'Uploading video'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(video.progress * 100)}
          >
            <Fill style={{ '--p': video.progress } as CSSProperties} />
          </Track>
        )}
      </Status>
      <Remove type="button" aria-label="Remove video" disabled={disabled} onClick={onRemove}>
        <Icon name="close" size={16} />
      </Remove>
    </Chip>
  );
}
